import AvatarGroup from "components/common/Avatar/AvatarGroup";
import Breadcrumb from "components/common/Breadcrumb/Breadcrumb";
import CircularLoader from "components/common/CircularLoader/CircularLoader";
import DateRangePickerNew from "components/common/DateRangePicker/DateRangePickerNew";
import Header from "components/common/Header/Header";
import PaginationComponent from "components/common/Pagination/index";
import PopoverStickOnHover from "components/common/Popover/PopoverStick";
import SelectOption from "components/common/Select/Select";
import SideBarNew from "components/common/SideBar/SideBarNew";
import TableHeader from "components/common/Table/TableHeader";
import {
  REPORTING_BREADCRUMB,
  REPORTING_BREADCRUMB_PAGES,
} from "Constants/BreadcrumbConstant";
import {
  emptyPaginationObj,
  getParticipantLabel,
  REPORTING_LIMIT,
  SORT_TYPE,
} from "Constants/constant";
import { LoginContext } from "helpers/LoginContext";
import { apiPostRequest } from "helpers/Requests";
import {
  INCIDENT_CATEGORY_LABELS,
  MAX_VISIBLE_CATEGORIES,
  OUTCOME_TYPE_OPTIONS,
  REPORTING_STATUS_OPTIONS,
} from "pages/IncidentManagement/constants";
import { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { URL_REPORTING_INCIDENT_MANAGEMENT } from "store/services/URL";
import { addMonths, capitalizeWords, exportTOExcel } from "Utils/utils";
import useReportExport from "../useReportExport";
import { withReportSortOnUrl } from "../reportExportUtils";
import {
  ReportFilterActions,
  ReportFilterField,
  ReportFilterGrid,
  ReportFilterSearchField,
  ReportFiltersShell,
  ReportPageContent,
  ReportTableSection,
} from "../reportFilterUi";

const IncidentCategoryCell = ({ incidentCategory }) => {
  const rawCategories =
    incidentCategory && incidentCategory !== "N/A"
      ? incidentCategory
          .split(", ")
          .map((c) => c.trim())
          .filter((c) => c && c !== "N/A")
      : [];

  const categories = rawCategories.map((c) => {
    if (INCIDENT_CATEGORY_LABELS[c]) return INCIDENT_CATEGORY_LABELS[c];
    if (c.includes("_")) return c.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
    return c;
  });

  if (categories.length === 0) {
    return (
      <span className="text-gray-400" aria-label="No categories">
        —
      </span>
    );
  }

  const visible = categories.slice(0, MAX_VISIBLE_CATEGORIES);
  const overflowCount = categories.length - MAX_VISIBLE_CATEGORIES;
  const overflowItems = overflowCount > 0 ? categories.slice(MAX_VISIBLE_CATEGORIES) : [];

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 max-w-[220px] min-w-0 overflow-hidden">
      {visible.map((label, idx) => (
        <span
          key={idx}
          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 shrink-0"
          title={label}
        >
          {label}
        </span>
      ))}
      {overflowCount > 0 && (
        <PopoverStickOnHover
          item={
            <div className="bg-white shadow-lg rounded-lg border border-gray-200 w-[220px] max-h-[240px] overflow-hidden flex flex-col">
              <div className="px-3 py-2 bg-gray-50 border-b border-gray-200 shrink-0">
                <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  {overflowCount} more categor{overflowCount === 1 ? "y" : "ies"}
                </span>
              </div>
              <ul className="space-y-1.5 text-sm text-gray-700 p-3 overflow-y-auto custom-scroll pr-4 flex-1 min-h-0">
                {overflowItems.map((label, idx) => (
                  <li key={idx} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400 shrink-0" />
                    {label}
                  </li>
                ))}
              </ul>
            </div>
          }
        >
          <span
            className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded text-[11px] font-medium bg-[#3E5B9B] text-white cursor-pointer hover:opacity-90 shrink-0"
            title={`${overflowCount} more categor${overflowCount === 1 ? "y" : "ies"}`}
          >
            +{overflowCount}
          </span>
        </PopoverStickOnHover>
      )}
    </div>
  );
};

const IncidentManagementReports = ({ getParticipantList, spParticipants, settings }) => {
  const { sideBarPadding, isUserGracePeriodOver } = useContext(LoginContext);
  const participantNameLabel = getParticipantLabel(settings?.care_sector, false) + " Name";
  const participantLabelPlural = getParticipantLabel(settings?.care_sector, true);
  const [searchParams] = useSearchParams();

  const [selectedFromDate, setSelectedFromDate] = useState(null);

  useEffect(() => {
    const filters = {
      page: 1,
      per_page: 500,
      search: null,
    };
    getParticipantList({ filters, bodyFilter: null });
  }, []);

  const [participantList, setParticipant] = useState([]);
  const [loadingParticipants, setLoadingParticipants] = useState(true);
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [selectedOutcomeType, setSelectedOutcomeType] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);

  const [search, setSearch] = useState(null);

  const [reportList, setReports] = useState(null);
  const [loader, setLoader] = useState(false);
  const [paginationObj, setPaginationObj] = useState(emptyPaginationObj);
  const [page, setPage] = useState(1);
  const [sortDescending, setSortDescending] = useState(
    paginationObj.sort_order === SORT_TYPE.DESC ? true : false,
  );
  const [sortBy, setSortBy] = useState(paginationObj.sort_field);

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [isDisableSearch, setIsDisableSearch] = useState(false);
  const [isInitialRender, setIsInitialRender] = useState(true);
  const { runExport, QueuedModal, getExportSortFilters } = useReportExport(
    "incident_management_report",
    "Incident Management Report",
    { sortBy, sortDescending }
  );

  // Track if dates came from URL params
  const [datesFromUrl, setDatesFromUrl] = useState(false);

  // Read date filters from URL params (e.g., from dashboard card click)
  useEffect(() => {
    const urlFromDate = searchParams.get("fromDate");
    const urlToDate = searchParams.get("toDate");
    if (urlFromDate && urlToDate) {
      setFromDate(new Date(urlFromDate));
      setToDate(new Date(urlToDate));
      setDatesFromUrl(true);
    }
  }, []);

  // Auto-fetch when dates are set from URL params
  useEffect(() => {
    if (datesFromUrl && fromDate && toDate) {
      fetchListReports();
      setDatesFromUrl(false);
    }
  }, [datesFromUrl, fromDate, toDate]);

  useEffect(() => {
    if (isInitialRender) {
      setIsInitialRender(false);
      return;
    }
    fetchListReports();
  }, [page]);

  useEffect(() => {
    const updateLists = async () => {
      setLoadingParticipants(true);
      const participants =
        spParticipants?.participant?.data?.map((item) => ({
          label: `${item?.first_name} ${item?.last_name}`,
          value: item?.id,
        })) || [];

      const sortedParticipants = participants.sort((a, b) =>
        a.label.toLowerCase().localeCompare(b.label.toLowerCase()),
      );

      setParticipant(sortedParticipants);
      setLoadingParticipants(false);
    };

    if (spParticipants?.participant?.data) {
      updateLists();
    }
  }, [spParticipants]);

  const fetchListReports = async (
    isClearAll = false,
    report = null,
    filters = {},
  ) => {
    if (!fromDate && !toDate) {
      toast("Select date range to get data");
      return;
    }
    const currentPage = filters.page || page;
    setLoader(true);
    let url = `${URL_REPORTING_INCIDENT_MANAGEMENT}?per_page=25&page=${currentPage}`;
    url = withReportSortOnUrl(url, filters, sortBy, sortDescending);
    const payload = {
      search: search ? search : "",
      participant: isClearAll
        ? "all"
        : selectedParticipant
          ? selectedParticipant?.value
          : "all",
      date_range: isClearAll
        ? "all"
        : toDate && fromDate
          ? [fromDate, toDate]
          : "all",
      outcome_type: isClearAll
        ? "all"
        : selectedOutcomeType
          ? selectedOutcomeType?.value
          : "all",
      status: isClearAll
        ? "all"
        : selectedStatus
          ? selectedStatus?.value
          : "all",
    };
    if (report) {
      await runExport({
        format: report,
        payload,
        syncUrl: url,
        filters,
        totalRows: paginationObj?.total,
        setLoader,
        onSyncExcel: exportTOExcelFile,
        onSyncPdf: (downloadUrl) => window.open(downloadUrl, "_blank"),
      });
      return;
    }
    await apiPostRequest(url, payload)
      .then((res) => {
        if (res.status === 200) {
          setLoader(false);
          setReports(res?.data?.data);
          setPaginationObj((prev) => ({
            ...prev,
            total: res?.data?.data?.incident_management_reports?.total,
            totalPages: Math.ceil(
              res?.data?.data?.incident_management_reports?.total /
                res?.data?.data?.incident_management_reports?.per_page,
            ),
          }));
        }
      })
      .catch((error) => {
        toast.error(`${error}`);
        console.log({ error });
        setLoader(false);
      });
  };

  const handleFileDownLoad = (e) => {
    fetchListReports(false, e, getExportSortFilters());
  };

  const handleSearch = () => {
    setPage(1);
    const updatedPaginationObj = { ...paginationObj, page: 1 };
    setPaginationObj(updatedPaginationObj);
    fetchListReports(false, null, {
      page: 1,
      sort_field: sortBy,
      sort_order: sortDescending ? SORT_TYPE.DESC : SORT_TYPE.ASC,
    });
  };

  const exportTOExcelFile = (reportData) => {
    const rows = Array.isArray(reportData?.incident_management_reports)
      ? reportData.incident_management_reports
      : reportData?.incident_management_reports?.data;

    const transformedData =
      rows &&
      rows.map((item) => {
        return {
          Incident_ID: item?.["Incident ID"] || item?.incident_id || "N/A",
          [participantLabelPlural]:
            item?.["Participants"] ||
            item?.["Participant(s)"] ||
            item?.[participantLabelPlural] ||
            item?.participant_names ||
            "N/A",
          Outcome_Type: item?.["Outcome Type"] || item?.outcome_type || "N/A",
          Status: item?.Status || item?.status || "N/A",
          Incident_Category:
            item?.["Incident Category (*PWD: person with disability)"] ||
            item?.["Incident Category"] ||
            item?.incident_category ||
            "N/A",
          Incident_Date:
            item?.["Incident Date"] || item?.incident_date || "N/A",
          Incident_Time:
            item?.["Incident Time"] || item?.incident_time || "N/A",
          Location: item?.Location || item?.incident_address || "N/A",
          Created_By: item?.["Created By"] || item?.created_by || "N/A",
          Created_Date: item?.["Created Date"] || item?.created_at || "N/A",
        };
      });
    if (
      !transformedData ||
      transformedData === undefined ||
      transformedData?.length === 0
    ) {
      toast("No data found!");
      return;
    }
    exportTOExcel(transformedData);
  };

  const handleFetch = (obj) => {
    fetchListReports(false, null, { ...obj });
  };

  const flipSort = (by) => {
    setSortBy(by);
    setSortDescending(!sortDescending);
    handleFetch({
      sort_field: by,
      sort_order: !sortDescending ? SORT_TYPE.DESC : SORT_TYPE.ASC,
    });
  };

  const clearFilters = () => {
    setFromDate(null);
    setToDate(null);
    setSearch(null);
    setReports([]);
    setSelectedFromDate(null);
    setSelectedParticipant(null);
    setSelectedOutcomeType(null);
    setSelectedStatus(null);
  };

  const handleDateRangeChange = (obj) => {
    if (obj) {
      obj.fromDate && setFromDate(obj.fromDate);
      obj.toDate && setToDate(obj.toDate);
    }
  };

  const handleDateCancel = () => {
    setFromDate(null);
    setToDate(null);
    setReports([]);
  };

  return (
    <div className="min-h-full">
      <SideBarNew />
      <div
        className={`${
          sideBarPadding ? "lg:pl-[4rem] duration-300" : "lg:pl-64 duration-300"
        }  flex flex-col `}
      >
        <main className="flex-1 bg-gray-50 min-h-screen">
          <Header
            title="Incident Management Reports"
            breadcrumb={
              <Breadcrumb
                pages={REPORTING_BREADCRUMB(
                  REPORTING_BREADCRUMB_PAGES.INCIDENT_MANAGEMENT_REPORTS,
                )}
              />
            }
          />
          <ReportPageContent>
            <ReportFiltersShell>
              <ReportFilterGrid columns={4}>
                <ReportFilterField label="Status">
                  <SelectOption
                    isClearable={true}
                    newDesign
                    cssClass="w-full"
                    data={REPORTING_STATUS_OPTIONS}
                    selectedOption={selectedStatus}
                    handleChange={(e) => setSelectedStatus(e)}
                    placeHolder="Select Status"
                  />
                </ReportFilterField>
                <ReportFilterField label="Outcome Type">
                  <SelectOption
                    isClearable={true}
                    newDesign
                    cssClass="w-full"
                    data={OUTCOME_TYPE_OPTIONS}
                    selectedOption={selectedOutcomeType}
                    handleChange={(e) => setSelectedOutcomeType(e)}
                    placeHolder="Select Outcome Type"
                  />
                </ReportFilterField>
                <ReportFilterField label={participantNameLabel.replace(" Name", "")}>
                  <SelectOption
                    isClearable={true}
                    newDesign
                    isDisabled={loadingParticipants}
                    cssClass="w-full"
                    name="participant"
                    isSearch={true}
                    data={participantList}
                    selectedOption={selectedParticipant}
                    handleChange={(e) => setSelectedParticipant(e)}
                    placeHolder={
                      loadingParticipants
                        ? `Loading ${participantLabelPlural.toLowerCase()}...`
                        : participantList?.length === 0
                          ? `No ${participantLabelPlural.toLowerCase()} available`
                          : `Select ${participantNameLabel.replace(" Name", "")}`
                    }
                    maxHeight="350px"
                  />
                </ReportFilterField>
                <ReportFilterField label="Date Range">
                  <DateRangePickerNew
                    placeholder="Select Date Range"
                    showCalendarIcon={true}
                    handleCancel={handleDateCancel}
                    handleChange={handleDateRangeChange}
                    fromDate={fromDate}
                    toDate={toDate}
                    setSelectedFromDate={setSelectedFromDate}
                    maxDate={addMonths(selectedFromDate, 3)}
                  />
                </ReportFilterField>
                <ReportFilterSearchField
                  label="Search"
                  search={search}
                  setSearch={setSearch}
                  onSearch={handleSearch}
                  placeholder="Search by ID, address, summary, NDIS reference..."
                />
              </ReportFilterGrid>
              <ReportFilterActions
                onSearch={handleSearch}
                onClear={clearFilters}
                onExport={handleFileDownLoad}
                isLoading={loader}
                isSearchDisabled={isDisableSearch}
                isUserGracePeriodOver={isUserGracePeriodOver}
              />
            </ReportFiltersShell>

            <ReportTableSection>
                <table className="border min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50 sticky z-[5]">
                    <tr>
                      <TableHeader
                        sortName="id"
                        flipSort={flipSort}
                        sortBy={sortBy}
                        sortDescending={sortDescending}
                        label={"Incident ID"}
                      />
                      <TableHeader isSort={false} label={participantLabelPlural} />
                      <TableHeader isSort={false} label={"Outcome Type"} />
                      <TableHeader isSort={false} label={"Status"} />
                      <TableHeader isSort={false} label={"Incident Category"} />
                      <TableHeader
                        sortName="incident_date"
                        flipSort={flipSort}
                        sortBy={sortBy}
                        sortDescending={sortDescending}
                        label={"Incident Date"}
                      />
                      <TableHeader isSort={false} label={"Location"} />
                      <TableHeader isSort={false} label={"Created By"} />
                      <TableHeader
                        sortName="created_at"
                        flipSort={flipSort}
                        sortBy={sortBy}
                        sortDescending={sortDescending}
                        label={"Created Date"}
                      />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {loader ? (
                      <tr className="bg-white">
                        <td colSpan={9} className="py-6 text-center bg-white">
                          <div
                            className={`flex flex-col w-full min-h-screen items-center justify-start`}
                          >
                            <CircularLoader
                              classes="flex justify-center items-center"
                              classloader="loader-l"
                            />
                          </div>
                        </td>
                      </tr>
                    ) : reportList !== null &&
                      reportList?.incident_management_reports?.data?.length >
                        0 ? (
                      reportList?.incident_management_reports?.data?.map(
                        (item, index) => (
                          <tr
                            key={item?.id || index}
                            className="even:bg-white odd:bg-gray-50"
                          >
                            <td className="whitespace-nowrap px-3 text-left py-4 text-sm font-medium">
                              <span className="text-blue-600 font-semibold">
                                {item?.incident_id || `IN-${item?.id}`}
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-3 text-left py-4 text-sm text-gray-500">
                              <div className="flex items-center">
                                {item?.participants &&
                                item?.participants?.length > 0 ? (
                                  item?.participants?.length === 1 ? (
                                    <span className="truncate whitespace-nowrap overflow-ellipsis block capitalize">
                                      {capitalizeWords(
                                        `${item?.participants[0]?.first_name || ""} ${item?.participants[0]?.last_name || ""}`.trim(),
                                      ) || "N/A"}
                                    </span>
                                  ) : (
                                    <AvatarGroup
                                      arrayData={item?.participants?.map(
                                        (p) => ({
                                          ...p,
                                          first_name: p?.first_name || "",
                                          last_name: p?.last_name || "",
                                          profile_picture: p?.profile_picture,
                                        }),
                                      )}
                                      cssClass="mt-0"
                                      maxLength={3}
                                      isNoRing={true}
                                    />
                                  )
                                ) : (
                                  <span className="text-gray-400 italic">
                                    N/A
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="whitespace-nowrap px-3 text-left py-4 text-sm text-gray-500">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  item?.outcome_type === "Hazard"
                                    ? "bg-yellow-100 text-yellow-800"
                                    : item?.outcome_type === "Near Miss"
                                      ? "bg-orange-100 text-orange-800"
                                      : item?.outcome_type === "Incident"
                                        ? "bg-red-100 text-red-800"
                                        : item?.outcome_type ===
                                            "Medication Incident"
                                          ? "bg-purple-100 text-purple-800"
                                          : "bg-gray-100 text-gray-800"
                                }`}
                              >
                                {item?.outcome_type || "N/A"}
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-3 text-left py-4 text-sm text-gray-500">
                              <span
                                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  item?.status === "Submitted"
                                    ? "bg-blue-100 text-blue-800"
                                    : item?.status === "Under Investigation"
                                      ? "bg-yellow-100 text-yellow-800"
                                      : item?.status === "Closed"
                                        ? "bg-green-100 text-green-800"
                                        : "bg-gray-100 text-gray-800"
                                }`}
                              >
                                {item?.status || "N/A"}
                              </span>
                            </td>
                            <td className="px-3 text-left py-4 text-sm text-gray-500 min-w-0 max-w-[240px]">
                              <IncidentCategoryCell
                                incidentCategory={item?.incident_category}
                              />
                            </td>
                            <td className="whitespace-nowrap px-3 text-left py-4 text-sm text-gray-500">
                              {item?.incident_date || "N/A"}
                            </td>
                            <td className="whitespace-nowrap px-3 text-left py-4 text-sm text-gray-500">
                              <span
                                className="block truncate max-w-[150px]"
                                title={item?.incident_address || ""}
                              >
                                {item?.incident_address || "-"}
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-3 text-left py-4 text-sm text-gray-500 capitalize">
                              <span
                                className="block truncate max-w-[120px]"
                                title={item?.created_by || ""}
                              >
                                {item?.created_by || "-"}
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-3 text-left py-4 text-sm text-gray-500">
                              {item?.created_at || "N/A"}
                            </td>
                          </tr>
                        ),
                      )
                    ) : (
                      <tr>
                        <td
                          colSpan={9}
                          className="py-6 text-center text-red-600"
                        >
                          No data found. Please adjust your filters to get
                          relevant results
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
            </ReportTableSection>

              {reportList &&
                reportList?.incident_management_reports?.data?.length > 0 && (
                  <div className="mt-3">
                    <PaginationComponent
                      page={paginationObj?.page || 1}
                      activeListLength={
                        reportList?.incident_management_reports?.data || []
                      }
                      limit={REPORTING_LIMIT}
                      totalList={paginationObj?.total}
                      handlePagination={(e) => {
                        setPage(e);
                        const obj = paginationObj;
                        obj.page = e;
                        setPaginationObj({ ...obj });
                      }}
                      totalPages={paginationObj?.totalPages}
                    />
                  </div>
                )}
          </ReportPageContent>
        </main>
      </div>
      <QueuedModal />
    </div>
  );
};

export default IncidentManagementReports;
